import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Coins } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";

const ROUTES = [
  [/^\/books\/([^/]+)\/proofread$/, "proofread", "book_id"],
  [/^\/books\/([^/]+)\/pipeline$/, "pipeline", "book_id"],
  [/^\/books\/([^/]+)\/fact-check$/, "fact_check", "book_id"],
  [/^\/books\/([^/]+)\/plagiarism-check$/, "plagiarism_check", "book_id"],
  [/^\/proof-jobs\/([^/]+)\/resume$/, "proof_resume", "job_id"],
  [/^\/books\/([^/]+)\/rewrite$/, "rewrite", "book_id"],
  [/^\/rewrite-jobs\/([^/]+)\/start$/, "rewrite_start", "job_id"],
  [/^\/rewrite-jobs\/([^/]+)\/resume$/, "rewrite_resume", "job_id"],
  [/^\/books\/([^/]+)\/continuity\/jobs$/, "continuity", "book_id"],
  [/^\/continuity\/jobs\/([^/]+)\/resume$/, "continuity_resume", "job_id"],
  [/^\/books\/([^/]+)\/write$/, "write", "book_id"],
  [/^\/books\/([^/]+)\/narration$/, "narration", "book_id"],
  [/^\/media-jobs\/([^/]+)\/resume$/, "media_resume", "job_id"],
  [/^\/chapter-audio\/([^/]+)\/transcribe$/, "transcribe", "audio_id"],
  [/^\/books\/([^/]+)\/cover-concepts$/, "cover_concepts", "book_id"],
  [/^\/comic-pages\/([^/]+)\/panels\/[^/]+\/draw$/, "comic_panel", "target_id"],
  [/^\/comic-pages\/([^/]+)\/draw-all$/, "comic_draw_all", "target_id"],
  [/^\/comic-characters\/([^/]+)\/sheet$/, "comic_sheet", "target_id"],
  [/^\/books\/([^/]+)\/comic\/script$/, "comic_script", "book_id"],
  [/^\/poems\/([^/]+)\/polish$/, "poem_polish", "target_id"],
  [/^\/poems\/([^/]+)\/illustration$/, "poem_illustration", "target_id"],
  [/^\/poems\/([^/]+)\/audio\/ai$/, "poem_audio", "target_id"],
  [/^\/research$/, "research", null],
  [/^\/audio\/preview$/, "audio_preview", null],
];
const PICK = ["chapter_ids", "model", "mode", "target_words", "min_words", "para_start", "para_end", "model_id", "text", "kind"];

let open = null;
const ask = (est) => new Promise((resolve) => open?.(est, resolve));

export function installCostPreview(api) {
  api.interceptors.request.use(async (cfg) => {
    if ((cfg.method || "").toLowerCase() !== "post" || cfg._costOk) return cfg;
    const url = (cfg.url || "").split("?")[0];
    const hit = ROUTES.find(([re]) => re.test(url));
    if (!hit) return cfg;
    const [re, action, idKey] = hit;
    const body = typeof cfg.data === "object" && cfg.data ? cfg.data : {};
    const req = { action, ...Object.fromEntries(PICK.filter((k) => body[k] != null).map((k) => [k, body[k]])) };
    if (idKey) req[idKey] = url.match(re)[1];
    if (action === "research") req.text = "";
    let est;
    try { est = (await api.post("/credits/estimate", req, { _costOk: true })).data; } catch { return { ...cfg, _costOk: true }; }
    if (est.unlimited) return { ...cfg, _costOk: true };
    const ok = await ask(est);
    if (!ok) { const e = new Error("Cancelled — no credits used."); e.cancelled = true; throw e; }
    setTimeout(() => window.dispatchEvent(new Event("credits-changed")), 8000);
    return { ...cfg, _costOk: true };
  });
}

export function CostPreviewDialog() {
  const [state, setState] = useState(null);
  useEffect(() => { open = (est, resolve) => setState({ est, resolve }); return () => { open = null; }; }, []);
  const done = (v) => { state?.resolve(v); setState(null); };
  const e = state?.est;
  return (
    <Dialog open={!!state} onOpenChange={(o) => !o && done(false)}>
      <DialogContent className="bg-slate-950 border-slate-800 max-w-md" data-testid="cost-preview-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Coins size={18} className="text-amber-400" />Confirm AI action</DialogTitle>
          <DialogDescription className="text-slate-400">{e?.label}</DialogDescription>
        </DialogHeader>
        {e && (
          <div className="space-y-3">
            <div className="panel p-4">
              <p className="text-xs text-slate-500">Estimated cost</p>
              <p className="font-display text-3xl text-amber-200" data-testid="cost-preview-credits">≈ {e.credits < 0.1 ? "< 0.1" : e.credits.toLocaleString()} credits</p>
              <p className="text-[11px] text-slate-500 mt-1" data-testid="cost-preview-range">Likely between {e.low.toLocaleString()} and {e.high.toLocaleString()}. You're charged the real amount after it runs; repeated identical requests are free.</p>
            </div>
            <p className="text-sm text-slate-300" data-testid="cost-preview-balance">Balance {e.balance.toLocaleString()} → about {Math.max(0, e.after).toLocaleString()} after</p>
            {!e.enough && <p className="text-xs text-amber-500" data-testid="cost-preview-low">You may not have enough credits to finish — long jobs pause when credits run out and resume after you top up. <Link to="/app/billing#packs" onClick={() => done(false)} className="underline text-amber-300">Top up</Link></p>}
          </div>
        )}
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" className="border-slate-700" onClick={() => done(false)} data-testid="cost-preview-cancel">Cancel</Button>
          <Button className="bg-amber-600 hover:bg-amber-500" onClick={() => done(true)} disabled={e && e.balance <= 0} data-testid="cost-preview-confirm">Run it</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
